import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Toolbar,
    IconButton,
    Tab,
    Tabs,
    Paper,
    Divider,
    Button,
    CircularProgress,
    Snackbar,
    Typography,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import EmailIcon from '@mui/icons-material/Email';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { Swiper, SwiperSlide } from 'swiper/react';
import { getListingImageUrl } from '../utils/imageUtils';
import { Thumbs, Navigation, Keyboard } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/thumbs';
import { useAuth } from '../context/AuthContext';
import { useApi } from '../api/useApi';
import UpdateProfileDialog from './UpdateBuyerProfileDialog';
import LogoutIcon from "@mui/icons-material/Logout";

const ListingDetailPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user, setUser, logout } = useAuth();
    const { authFetch } = useApi();

    // Profile dialog state
    const [profileOpen, setProfileOpen] = useState(false);

    const [listing, setListing] = useState(null);
    const [loading, setLoading] = useState(true);
    const [tabValue, setTabValue] = useState(0);
    const [thumbsSwiper, setThumbsSwiper] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // Offer dialog state
    const [offerOpen, setOfferOpen] = useState(false);
    const [offerAmount, setOfferAmount] = useState('');
    const [offerCurrency, setOfferCurrency] = useState('EUR');
    const [offerSubmitting, setOfferSubmitting] = useState(false);

    const handleLogout = () => {
        logout();
        navigate("/login");
    };

    useEffect(() => {
        const fetchListing = async () => {
            try {
                const res = await authFetch(`/listings/${id}`, {
                    headers: { Accept: 'application/json' }
                });
                if (!res.ok) throw new Error('Fetch failed');
                const data = await res.json();
                setListing(data);
            } catch (e) {
                console.error(e);
                setSnackbar({ open: true, message: 'Could not load listing', severity: 'error' });
            } finally {
                setLoading(false);
            }
        };
        fetchListing();
    }, [id, authFetch]);

    const handleTabChange = (_, newVal) => setTabValue(newVal);
    const handleCloseSnackbar = () => setSnackbar(prev => ({ ...prev, open: false }));

    const handleProfileOpen = () => setProfileOpen(true);
    const handleProfileClose = () => setProfileOpen(false);

    const handleOpenOffer = () => {
        setOfferAmount('');
        setOfferCurrency('EUR');
        setOfferOpen(true);
    };

    const handleCloseOffer = () => {
        if (offerSubmitting) return;
        setOfferOpen(false);
    };

    const submitOffer = async () => {
        if (!listing || !user?.id) {
            setSnackbar({ open: true, message: 'Missing user or listing', severity: 'error' });
            return;
        }

        // Keep as string to play nice with BigDecimal on backend.
        // Allow decimal comma -> normalize to dot.
        const normalized = String(offerAmount).replace(',', '.').trim();

        if (!normalized) {
            setSnackbar({ open: true, message: 'Please enter an amount', severity: 'error' });
            return;
        }

        const asNumber = Number(normalized);
        if (!Number.isFinite(asNumber) || asNumber <= 0) {
            setSnackbar({ open: true, message: 'Enter a valid amount', severity: 'error' });
            return;
        }

        setOfferSubmitting(true);
        try {
            const res = await authFetch('/user/make-offer', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    listingId: listing.id,
                    buyerId: user.id,
                    offerAmount: normalized, // BigDecimal-friendly
                    currency: offerCurrency,
                }),
            });

            if (!res.ok) throw new Error('Offer failed');

            setSnackbar({ open: true, message: 'Offer sent', severity: 'success' });
            setOfferOpen(false);
        } catch (e) {
            console.error(e);
            setSnackbar({ open: true, message: 'Could not submit offer', severity: 'error' });
        } finally {
            setOfferSubmitting(false);
        }
    };

    if (loading) return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
            <CircularProgress />
        </Box>
    );
    if (!listing) return null;

    const { car, listingTitle, description, adminContact, price } = listing;
    const {
        images = [],
        make,
        model,
        variant,
        hp,
        engine,
        type,
        color,
        fuelType,
        doors,
        seats,
        firstRegistrationDate,
        km,
        vatReclaimable,
        vin
    } = car;

    return (
        <React.Fragment>
            {/* Full-width Top Toolbar */}
            <Toolbar sx={{ justifyContent: 'flex-end', borderBottom: '1px solid', borderColor: 'divider' }}>
                {/* Centered Logo */}
                <Box
                    component="img"
                    src={`${process.env.PUBLIC_URL}/logo.png`}
                    alt="Logo"
                    sx={{
                        position: 'absolute',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        height: 36
                    }}
                />
                {user?.userType === 'BUYER' ?
                    <React.Fragment>
                        <Tooltip title="Profile">
                            <IconButton onClick={handleProfileOpen}>
                                <AccountCircleIcon fontSize="large" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Logout">
                            <IconButton onClick={handleLogout}>
                                <LogoutIcon fontSize="large" />
                            </IconButton>
                        </Tooltip>
                    </React.Fragment>
                    : <Tooltip title="Back to admin listings">
                        <IconButton onClick={() => navigate("/admin/listings")}>
                            <ArrowBackIcon />
                        </IconButton>
                    </Tooltip>}
            </Toolbar>

            {/* Profile Dialog */}
            <UpdateProfileDialog
                open={profileOpen}
                onClose={handleProfileClose}
                user={user}
                onUpdated={newUser => setUser(newUser)}
            />

            {/* Centered content with max width */}
            <Box sx={{ maxWidth: 900, mx: 'auto', my: 3 }}>
                <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
                    {/* Back Button */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <IconButton onClick={() => navigate('/buyer/listings')}>
                            <ArrowBackIcon />
                        </IconButton>
                        <Typography variant="body1" sx={{ ml: 1 }}>Back to All Listings</Typography>
                    </Box>

                    {/* Main carousel */}
                    <Swiper
                        modules={[Thumbs, Navigation, Keyboard]}
                        spaceBetween={10}
                        slidesPerView={1}
                        thumbs={{ swiper: thumbsSwiper }}
                        navigation={{
                            nextEl: '.swiper-button-next',
                            prevEl: '.swiper-button-prev',
                        }}
                        keyboard={{ enabled: true }}
                        loop={true}
                        style={{ borderRadius: 8, overflow: 'hidden', marginBottom: 16 }}
                    >
                        {images.map((img) => (
                            <SwiperSlide key={img.id}>
                                <Box
                                    component="img"
                                    src={img.path}
                                    alt={listingTitle}
                                    sx={{
                                        width: '100%',
                                        maxHeight: 550,
                                        objectFit: 'contain',
                                    }}
                                />
                            </SwiperSlide>
                        ))}

                        {/* Navigation arrows */}
                        <div className="swiper-button-prev" />
                        <div className="swiper-button-next" />

                        {/* Custom arrow color */}
                        <style jsx>{`
                            .swiper-button-next,
                            .swiper-button-prev {
                              color: #003653 !important;
                            }
                        `}</style>
                    </Swiper>

                    {/* Thumbnails */}
                    <Swiper
                        modules={[Thumbs]}
                        onSwiper={setThumbsSwiper}
                        spaceBetween={10}
                        slidesPerView={3}
                        style={{ marginBottom: 16 }}
                    >
                        {images.map((img) => (
                            <SwiperSlide key={img.id}>
                                <Box
                                    component="img"
                                    src={getListingImageUrl(img.path, 'thumbnail')}
                                    alt="thumb"
                                    sx={{
                                        width: '100%',
                                        height: 80,
                                        objectFit: 'cover',
                                        borderRadius: 1,
                                        cursor: 'pointer',
                                        border: '2px solid transparent',
                                        '&:hover': { borderColor: 'primary.main' },
                                    }}
                                />
                            </SwiperSlide>
                        ))}
                    </Swiper>

                    {/* Title & Variant */}
                    {/* Title row with Make Offer */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 2,
                            flexWrap: 'wrap', // handles small screens nicely
                        }}
                    >
                        <Box>
                            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                                {make} {model}
                            </Typography>
                            <Typography variant="subtitle1" color="text.secondary">
                                {variant}
                            </Typography>
                        </Box>
                        <Button
                            variant="contained"
                            size="medium"
                            onClick={handleOpenOffer}
                            sx={{ whiteSpace: 'nowrap' }}
                        >
                            Make offer
                        </Button>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    {/* Tabs Section */}
                    <Tabs value={tabValue} onChange={handleTabChange} variant="fullWidth">
                        <Tab label="Specifications" />
                        <Tab label="Description" />
                        <Tab label="Contact" />
                    </Tabs>

                    <Box sx={{ p: 3 }}>
                        {tabValue === 0 && (
                            <Box>
                                <Typography><strong>First Registration:</strong> {firstRegistrationDate ? new Date(firstRegistrationDate).toLocaleDateString() : '-'}</Typography>
                                <Typography><strong>Price:</strong> {price}</Typography>
                                <Typography><strong>VAT Reclaimable:</strong> {vatReclaimable ? 'Yes' : 'No'}</Typography>
                                <Typography><strong>Km:</strong> {km}</Typography>
                                <Typography><strong>Horsepower:</strong> {hp} HP</Typography>
                                <Typography><strong>Color:</strong> {color}</Typography>
                                <Typography><strong>Fuel Type:</strong> {fuelType}</Typography>
                                <Typography><strong>Engine:</strong> {engine}</Typography>
                                <Typography><strong>Type:</strong> {type}</Typography>
                                <Typography><strong>Doors:</strong> {doors}</Typography>
                                <Typography><strong>Seats:</strong> {seats}</Typography>
                                <Typography><strong>VIN:</strong> {vin}</Typography>
                            </Box>
                        )}

                        {tabValue === 1 && (
                            <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                                {description}
                            </Typography>
                        )}

                        {tabValue === 2 && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Box>
                                    <Typography variant="h6" sx={{ mb: 1 }}>Contact sales</Typography>
                                    <Typography><strong>Name:</strong> {adminContact.fullName}</Typography>
                                    <Typography><strong>Email:</strong> {adminContact.email}</Typography>
                                    <Typography><strong>WhatsApp:</strong> {adminContact.whatsapp}</Typography>
                                </Box>
                                <Button variant="contained" startIcon={<EmailIcon />} href={`mailto:${adminContact.email}`}>
                                    Send Email
                                </Button>
                                <Button
                                    variant="contained"
                                    color="success"
                                    startIcon={<WhatsAppIcon />}
                                    href={`https://wa.me/${adminContact.whatsapp.replace(/\D/g, '')}`}
                                    target="_blank"
                                >
                                    Chat via WhatsApp
                                </Button>
                            </Box>
                        )}
                    </Box>
                </Paper>
            </Box>

            {/* Offer dialog */}
            <Dialog open={offerOpen} onClose={handleCloseOffer} fullWidth maxWidth="xs">
                <DialogTitle>Make an offer</DialogTitle>
                <DialogContent dividers>
                    <TextField
                        label="Amount"
                        value={offerAmount}
                        onChange={(e) => setOfferAmount(e.target.value)}
                        fullWidth
                        margin="dense"
                        type="number"
                        inputMode="decimal"
                        placeholder="e.g. 25000"
                        InputProps={{
                            inputProps: {
                                min: 0,
                                step: '0.01',
                            }
                        }}
                    />

                    <TextField
                        select
                        label="Currency"
                        value={offerCurrency}
                        onChange={(e) => setOfferCurrency(e.target.value)}
                        fullWidth
                        margin="dense"
                    >
                        {['EUR', 'DKK', 'SEK', 'NOK', 'GBP', 'USD'].map(c => (
                            <MenuItem key={c} value={c}>{c}</MenuItem>
                        ))}
                    </TextField>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseOffer} disabled={offerSubmitting}>
                        Cancel
                    </Button>
                    <Button variant="contained" onClick={submitOffer} disabled={offerSubmitting}>
                        {offerSubmitting ? <CircularProgress size={18} /> : 'Submit offer'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                <MuiAlert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </MuiAlert>
            </Snackbar>
        </React.Fragment>
    );
};

export default ListingDetailPage;
